import { useConfirm } from '@/components/Confirm';
/**
 * Migration 0083 — /settings/ops-alerts
 *
 * Slack / Discord / Teams channel subscriptions for ops alerts. Operators
 * paste a webhook URL from their chat tool, pick which events fire to that
 * channel, and (optionally) send a test message to verify wiring before
 * trusting it for real incidents.
 */
import { ExternalLink } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

type ChannelType = 'slack' | 'discord' | 'teams';
type Severity = 'info' | 'warning' | 'critical';

interface EventCatalogueEntry {
  key: string;
  label: string;
  description: string;
  severity: Severity;
}

interface SubscriptionRow {
  id: string;
  channelType: ChannelType;
  webhookUrl: string;
  name: string;
  eventSet: string[];
  isActive: boolean;
  lastDeliveredAt: string | null;
  lastFailureReason: string | null;
  failureCount: number;
  createdAt: string;
  updatedAt: string;
}

const CHANNEL_HINT: Record<ChannelType, { name: string; example: string; docsHref: string }> = {
  slack: {
    name: 'Slack',
    example: 'https://hooks.slack.com/services/T0…/B0…/…',
    docsHref: 'https://api.slack.com/messaging/webhooks',
  },
  discord: {
    name: 'Discord',
    example: 'https://discord.com/api/webhooks/…/…',
    docsHref: 'https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks',
  },
  teams: {
    name: 'Microsoft Teams',
    example: 'https://outlook.office.com/webhook/…',
    docsHref:
      'https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook',
  },
};

export function OpsAlertsSettings() {
  const [rows, setRows] = useState<SubscriptionRow[]>([]);
  const [catalogue, setCatalogue] = useState<EventCatalogueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Create form state.
  const [channelType, setChannelType] = useState<ChannelType>('slack');
  const [name, setName] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);

  const { confirm, ConfirmDialog } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subsRes, evtRes] = await Promise.all([
        fetch('/api/ops-alerts', { credentials: 'include' }),
        fetch('/api/ops-alerts/events', { credentials: 'include' }),
      ]);
      if (subsRes.ok) {
        const body = (await subsRes.json()) as { subscriptions: SubscriptionRow[] };
        setRows(body.subscriptions);
      }
      if (evtRes.ok) {
        const body = (await evtRes.json()) as { events: EventCatalogueEntry[] };
        setCatalogue(body.events);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = useCallback(async () => {
    if (!webhookUrl || !name || selectedEvents.length === 0) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const res = await fetch('/api/ops-alerts', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          channelType,
          webhookUrl,
          name,
          eventSet: selectedEvents,
        }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as {
          error?: string;
          issues?: string[];
        };
        setError(err?.issues?.join(', ') ?? err?.error ?? 'Create failed.');
        return;
      }
      setName('');
      setWebhookUrl('');
      setSelectedEvents([]);
      setInfo('Channel added. Send a test message to verify it works.');
      await load();
    } finally {
      setSaving(false);
    }
  }, [channelType, webhookUrl, name, selectedEvents, load]);

  const toggleActive = useCallback(
    async (row: SubscriptionRow) => {
      setError(null);
      const res = await fetch(`/api/ops-alerts/${row.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ isActive: !row.isActive }),
      });
      if (!res.ok) {
        setError(`Could not ${row.isActive ? 'disable' : 'enable'} "${row.name}". Try again.`);
        return;
      }
      await load();
    },
    [load],
  );

  const sendTest = useCallback(
    async (row: SubscriptionRow) => {
      setInfo(null);
      setError(null);
      const res = await fetch(`/api/ops-alerts/${row.id}/test`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        setError('Test failed — see the failure details on the row.');
        await load();
        return;
      }
      const body = (await res.json()) as {
        result: { delivered: number; failed: number; disabled: number };
      };
      if (body.result.delivered > 0) {
        setInfo(`Test sent to ${row.name}. Check the channel.`);
      } else {
        setError(`Test failed for ${row.name}. ${body.result.failed} failure(s) recorded.`);
      }
      await load();
    },
    [load],
  );

  const remove = useCallback(
    async (row: SubscriptionRow) => {
      const yes = await confirm({
        title: 'Delete this alert channel?',
        message: `Remove "${row.name}"? Events will stop firing to this channel immediately.`,
        confirmLabel: 'Delete',
        danger: true,
      });
      if (!yes) return;
      await fetch(`/api/ops-alerts/${row.id}`, { method: 'DELETE', credentials: 'include' });
      await load();
    },
    [confirm, load],
  );

  return (
    <div className="space-y-8">
      <ConfirmDialog />
      <header>
        <h1 className="text-2xl font-semibold">Ops alerts</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Forward critical events to Slack, Discord, or Microsoft Teams. Get pinged when a payment
          fails, an incident is logged, or a customer leaves a low NPS score.
        </p>
      </header>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
      {info && (
        <div className="rounded-md border border-success/30 bg-success/10 px-3 py-2 text-sm text-success">
          {info}
        </div>
      )}

      {/* --- Existing channels --- */}
      <section className="space-y-3">
        <h2 className="text-lg font-medium">Channels</h2>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No channels configured yet. Add your first below.
          </p>
        ) : (
          <ul className="divide-y rounded-md border bg-card">
            {rows.map((row) => (
              <li key={row.id} className="space-y-2 px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-medium">{row.name}</span>
                    <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs uppercase tracking-wide text-muted-foreground">
                      {row.channelType}
                    </span>
                    {!row.isActive && (
                      <span className="ml-2 rounded bg-destructive/10 px-1.5 py-0.5 text-xs uppercase tracking-wide text-destructive">
                        Disabled
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="rounded border px-2 py-1 text-xs hover:bg-muted"
                      onClick={() => void sendTest(row)}
                    >
                      Send test
                    </button>
                    <button
                      type="button"
                      className="rounded border px-2 py-1 text-xs hover:bg-muted"
                      onClick={() => void toggleActive(row)}
                    >
                      {row.isActive ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      type="button"
                      className="rounded border border-destructive/30 px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
                      onClick={() => void remove(row)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div className="truncate">{row.webhookUrl}</div>
                  <div>Events: {row.eventSet.length === 0 ? 'none' : row.eventSet.join(', ')}</div>
                  <div>
                    Last delivered:{' '}
                    <span className="num">
                      {row.lastDeliveredAt
                        ? new Date(row.lastDeliveredAt).toLocaleString()
                        : 'never'}
                    </span>
                    {row.failureCount > 0 && (
                      <span className="ml-2 text-destructive">
                        — <span className="num">{row.failureCount}</span> recent failure(s)
                        {row.lastFailureReason ? `: ${row.lastFailureReason}` : ''}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* --- Add a channel --- */}
      <form
        className="space-y-4 rounded-md border bg-card p-4"
        onSubmit={(e) => {
          e.preventDefault();
          void create();
        }}
      >
        <h2 className="text-lg font-medium">Add a channel</h2>

        <div className="grid gap-3 md:grid-cols-2">
          <label className="space-y-1 text-sm">
            <span className="font-medium">Channel type</span>
            <select
              className="w-full rounded border px-2 py-1.5"
              value={channelType}
              onChange={(e) => setChannelType(e.target.value as ChannelType)}
            >
              <option value="slack">Slack</option>
              <option value="discord">Discord</option>
              <option value="teams">Microsoft Teams</option>
            </select>
          </label>

          <label className="space-y-1 text-sm">
            <span className="font-medium">Channel name</span>
            <input
              type="text"
              className="w-full rounded border px-2 py-1.5"
              placeholder='e.g. "Front-of-house Slack"'
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <label className="space-y-1 text-sm md:col-span-2">
            <span className="font-medium">Webhook URL</span>
            <input
              type="url"
              className="w-full rounded border px-2 py-1.5 font-mono text-xs"
              placeholder={CHANNEL_HINT[channelType].example}
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
            />
            <span className="text-xs text-muted-foreground">
              Need help?{' '}
              <a
                href={CHANNEL_HINT[channelType].docsHref}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                How to get a {CHANNEL_HINT[channelType].name} webhook URL{' '}
                <ExternalLink className="inline size-3" strokeWidth={1.75} />
              </a>
            </span>
          </label>
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium">Which events should fire here?</h3>
          <p className="text-xs text-muted-foreground">
            Pick narrowly — chat-channel noise drives mute. Most teams want 2–4 events per channel.
          </p>
          <ul className="space-y-1">
            {catalogue.map((evt) => {
              const checked = selectedEvents.includes(evt.key);
              return (
                <li key={evt.key}>
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={checked}
                      onChange={() => {
                        setSelectedEvents((prev) =>
                          checked ? prev.filter((k) => k !== evt.key) : [...prev, evt.key],
                        );
                      }}
                    />
                    <span>
                      <span className="font-medium">{evt.label}</span>
                      <span className="ml-1 text-xs text-muted-foreground">({evt.severity})</span>
                      <span className="block text-xs text-muted-foreground">{evt.description}</span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Link
            to="/settings/integrations"
            className="text-xs text-muted-foreground hover:underline"
          >
            Back to integrations
          </Link>
          <button
            type="submit"
            className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
            disabled={saving || !webhookUrl || !name || selectedEvents.length === 0}
          >
            {saving ? 'Saving…' : 'Add channel'}
          </button>
        </div>
      </form>
    </div>
  );
}
